import classNames from "classnames";
import React, { CSSProperties, useMemo } from "react";
import { useElementId } from "../../hooks";
import { LightDarkVariant } from "../types";
import { NavMenuContext } from "./nav-menu-context";
import { CSS_CLASSES } from "./nav-menu-css-classes";
import { useNavMenu } from "./use-nav-menu-hook";

export interface HorizontalMainNavMenuProps {
  /**
   * A unique `id` for this component.
   *
   * If not supplied, an internal, auto-generated unique id is used.
   */
  id?: string;

  /**
   * A label for this navigation component.
   *
   * This is not displayed visually. It is only for use by assistive technologies/screen readers for
   * improved accessibility.
   */
  label: string;

  /**
   * Visual variant - light background with dark text or vice versa.
   *
   * @default light
   */
  variant?: LightDarkVariant;

  /**
   * Color option for the when `variant="light"`. Ignored for `variant="dark"`.
   *
   * @default interactive
   */
  color?: "interactive" | "primary";

  /**
   * This prop controls whether the main nav menu item reveals its sub-menu pop-up (if present) on `hover`.
   *
   * If set to `false` the sub-menu is revealed on `click`.
   *
   * @default true
   */
  openSubMenuOnHover?: boolean;

  /**
   * One or more instances of the `HorizontalMainNavMenuItem` component.
   */
  children?: React.ReactChild[] | React.ReactChild;

  /**
   * Apply custom CSS class to the root element rendered by this component.
   *
   * This can be used for layout and positioning of this component within a larger application page layout.
   * It can also be used to enforce a scope for all CSS class names used internally within this component, and then
   * they can be selectively overridden if necessary using the applied root class name for specificity.
   */
  className?: string;

  /**
   * Inline styles applied to the root element rendered by this component.
   */
  style?: CSSProperties;
}

export type HorizontalMainNavMenuPropsForMenuItems = Pick<HorizontalMainNavMenuProps, "variant" | "color">;

/**
 * The `HorizontalMainNavMenu` component represents a menu of navigation links. It meant to be used for the main site
 * navigation menu.
 */
export const HorizontalMainNavMenu = ({
  id,
  label,
  variant = "light",
  color = "interactive",
  openSubMenuOnHover = true,
  children,
  className,
  style,
  ...restProps
}: HorizontalMainNavMenuProps) => {
  const mainNavMenuId = useElementId(id, "horizontal-main-nav-menu");

  const hasMenuItems = useMemo(() => !!children && React.Children.count(children) > 0, [children]);

  const propsForMenuItems = useMemo(
    () =>
      ({
        variant,
        color,
      } as HorizontalMainNavMenuPropsForMenuItems),
    [color, variant]
  );

  const navMenuContextValue = useNavMenu<HorizontalMainNavMenuPropsForMenuItems>(
    mainNavMenuId,
    label,
    children,
    propsForMenuItems
  );

  const navMenuClassName = useMemo(
    () =>
      classNames(
        CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_ROOT,
        {
          [CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_COLOR_INTERACTIVE]: color === "interactive",
          [CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_COLOR_PRIMARY]: color === "primary",
          [CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_OPEN_ON_CLICK]: !openSubMenuOnHover,
          [CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_OPEN_ON_HOVER]: openSubMenuOnHover,
        },
        className
      ),
    [className, color, openSubMenuOnHover]
  );

  return (
    <NavMenuContext.Provider value={navMenuContextValue}>
      <nav aria-label={label} className={navMenuClassName} style={style} {...restProps}>
        {hasMenuItems && <ul className={CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_LIST}>{children}</ul>}
      </nav>
    </NavMenuContext.Provider>
  );
};
